import { ArticleType, PurchaseCountry } from '@nemuru-eng/js-api-client';
import { useMemo } from 'react';

import { CartEntity } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/entities/CartEntity';
import { CartRepositoryFactory } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/entities/CartRepository/CartRepositoryFactory';
import { Item } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/entities/Item';
import { FormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/types/FormItem';
import { MinimalFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/types/MinimalFormItem';
import { StoredCart } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/types/StoredCart';
import { AbstractPaymentMethod } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/AbstractPaymentMethod';
import { AutoLoanPaymentMethod } from '@/components/modules/Application/components/PaymentMethod/AutoLoan/entities/PaymentMethod/AutoLoanPaymentMethod';
import { useApplicationContext } from '@/contexts/ApplicationContext/ApplicationContext';

export const useCart = () => {
  const {
    state: { cart, isCartFormValid, doSubmitCart, orderReference1, selectedAgent, checkoutSessionId, cartSummaryData },
    actions: { updateApplicationContext },
  } = useApplicationContext();

  const cartRepository = CartRepositoryFactory.getRepository();
  const storedCarts = selectedAgent ? cartRepository.get(selectedAgent.id) : [];

  const cartIsValid = useMemo(() => cart?.isReadyToSubmit() && isCartFormValid, [cart, isCartFormValid]);

  const setCart = (items: MinimalFormItem[], oldCart?: CartEntity): void => {
    const initialCart = oldCart ?? cart;
    if (!initialCart) return;
    const newCart = CartEntity.updateCartItemsFromMinimalForm(items, initialCart);
    updateCart(newCart);
  };

  const setCartItem = (item: FormItem, index: number): Item<FormItem> => {
    const newCart = CartEntity.updateCartItemFromDialogForm(item, index, cart as CartEntity);
    updateCart(newCart);
    return newCart.items[index];
  };

  const removeCartItem = (index: number): void => {
    const newCart = CartEntity.removeCartItem(index, cart as CartEntity);
    updateCart(newCart);
  };

  const setCartRequiredArticleTypes = (requiredArticleTypes: ArticleType[]): void => {
    if (!cart) return;
    const newCart = CartEntity.updateRequiredArticleTypes(requiredArticleTypes, cart);
    updateCart(newCart);
  };

  const createNewCartFromItems = (items: Item<FormItem>[], purchaseCountry: PurchaseCountry): CartEntity => {
    const newCart = CartEntity.createCart(items, purchaseCountry);
    updateCart(newCart);
    return newCart;
  };

  const createNewCartFromStoredCart = (cart: StoredCart): CartEntity | void => {
    if (!cart) return;
    const newCart = CartEntity.createNewCartFromStoredCart(cart);
    updateCart(newCart);
    return newCart;
  };

  const submitCart = () => updateDoSubmitCart(true);

  const clearSubmitCart = () => updateDoSubmitCart(false);
  const disableSubmitCart = useMemo(() => !cartIsValid || !orderReference1, [cartIsValid, orderReference1]);

  const updateDoSubmitCart = (doSubmitCart: boolean) => {
    if (cart && checkoutSessionId && selectedAgent) {
      if (!storedCarts.some((option) => JSON.stringify(option.cart) === JSON.stringify(cart))) {
        cartRepository?.set(selectedAgent.id, checkoutSessionId, {
          cart,
          createdAt: new Date(),
          id: checkoutSessionId,
        });
      }
    }
    updateApplicationContext({ doSubmitCart });
  };

  const updateCartDiscount = (paymentMethodEntities: AbstractPaymentMethod[] | undefined) => {
    if (!cart || !paymentMethodEntities) return;

    const discount =
      paymentMethodEntities.find((paymentMethodEntity) => paymentMethodEntity instanceof AutoLoanPaymentMethod)
        ?.selectedConditionsGroup?.discount?.value ?? 0;

    const newCart = cart.updateDiscount(discount);

    updateApplicationContext({
      cart: newCart,
    });
  };

  const updateCart = (cart: CartEntity) => updateApplicationContext({ cart });

  return {
    cart,
    cartIsValid,
    setCart,
    setCartItem,
    removeCartItem,
    setCartRequiredArticleTypes,
    submitCart,
    clearSubmitCart,
    disableSubmitCart,
    doSubmitCart,
    isCartFormValid,
    isReferenceValid: !!orderReference1,
    createNewCartFromItems,
    createNewCartFromStoredCart,
    cartSummaryData,
    updateCartDiscount,
  };
};
